<template>
  <custom-dialog
    ref="dialog"
    store-modal
    color="secondary"
    width="600px"
    :icon="generateMessagePath('icon')"
    :title="generateMessagePath('title')"
  >
    <v-form
      ref="form"
      v-model="valid"
      @input="$emit('valid', valid)"
      lazy-validation
    >
      <v-container>
        <v-row justify="center">
          <v-col cols="12" sm="6">
            <v-autocomplete
              outlined
              dense
              :items="types"
              item-text="name"
              item-value="id"
              v-model="item.exchange_type"
              :rules="[
                (v) => !!v || generateMessage('user_exchanges.create.notype'),
              ]"
              :label="generateMessage('user_exchanges.create.type') + ' *'"
              required
            ></v-autocomplete>
          </v-col>
          <v-col cols="12" sm="6">
            <v-text-field
              label="Date"
              dense
              hide-details
              outlined
              v-model="item.datetime"
              type="datetime-local"
              min="2010-06-07T00:00"
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <custom-authors-selector
              :placeholder="generateMessage('user_exchanges.create.author')"
              v-model="item.author_idx"
            />
          </v-col>
          <v-col cols="12">
            <custom-text-area
              v-model="item.description"
              :label="generateMessage('user_exchanges.create.description')"
            />
          </v-col>
          <v-col cols="12">
            <v-btn @click="handleAddLogbookLinkClick">
              {{
                generateMessage(
                  `user_exchanges.${
                    item.logbook_entry_id ? 'update' : 'create'
                  }.logbook`
                )
              }}
            </v-btn>
            {{ item.logbook_entry_id }}
            <v-dialog
              v-model="dialog"
              fullscreen
              overlay-color="white"
              transition="dialog-bottom-transition"
              scrollable
            >
              <user-exchange-form-link-logbook
                :value="item.logbook_entry_id"
                @input="handleLogbookItemSelected"
                @close="handleClose"
              />
            </v-dialog>
          </v-col>
        </v-row>
      </v-container>
    </v-form>
    <template v-slot:actions>
      <v-btn
        class="mr-5"
        :disabled="!valid"
        small
        color="success"
        @click="handleValidateClick"
        :loading="isEditing ? isLoadingUpdate : isLoadingCreate"
      >
        {{ generateMessagePath('validate') }}
      </v-btn>
    </template>
  </custom-dialog>
</template>

<script>
import CustomTextArea from '@/components/CustomTextArea';
import formComponent from '@/business/mixins/form';
import UserExchangeFormLinkLogbook from '@/pages/actionplan/actions/UserExchangeFormLinkLogbook';
import CustomAuthorsSelector from '@/components/selects/CustomAuthorsSelector';
import { mapGetters } from 'vuex';
import { generateMessage } from '@/utils/i18n';
import {
  formatDateToInputDateTime,
  getCurrentLocalDate,
  getCurrentLocalDateTime,
} from '@/business/utils/dates';
import CustomDialog from '@/components/dialogs/CustomDialog';

export default {
  name: 'UserExchangeFormDialog',
  mixins: [
    formComponent({
      namespace: 'actionplans/actions/user_exchanges',
    }),
  ],
  components: {
    UserExchangeFormLinkLogbook,
    CustomTextArea,
    CustomDialog,
    CustomAuthorsSelector,
  },
  computed: {
    ...mapGetters({
      types: 'actionplans/actions/user_exchanges/types/items',
      currentAction: 'actionplans/actions/current',
    }),
  },
  watch: {
    newItem(item) {
      if (item && item.id) {
        this.reset();
        this.$store.dispatch(
          'actionplans/actions/setStatuWhenUserExchangeCreated'
        );
        this.$store.dispatch('actionplans/setStatuWhenUserExchangeCreated');
        this.$emit('close');
      }
    },
    currentAction() {
      this.initItemFromNullItem();
      this.$refs.form.resetValidation();
    },
  },
  created() {
    this.$store.dispatch(
      'properties/initCurrent',
      this.currentAction.node_associated_id
    );
    this.$store.dispatch('logbook/setFilterAttribute', {
      attribute: 'property_id',
      serverSide: true,
      values: [this.currentAction.node_associated_id],
    });
  },
  data() {
    return {
      dialog: false,
    };
  },
  methods: {
    generateMessage,
    getCurrentLocalDate,
    getCurrentLocalDateTime,
    handleClose() {
      this.dialog = false;
      this.$store.dispatch('logbook/setEditMode', false);
    },
    handleLogbookItemSelected(item) {
      this.item.logbook_entry_id = item.id;
      this.$store.dispatch('logbook/setEditMode', false);
      this.dialog = false;
    },
    handleAddLogbookLinkClick() {
      this.dialog = true;
      this.$store.dispatch('logbook/setEditMode', true);
    },
    initItemFromNullItem() {
      this.item = {
        description: '',
        datetime: this.getCurrentLocalDateTime(),
        exchange_type: null,
        action_id: this.currentAction.id,
        author_idx: [],
        logbook_entry_id: null,
      };
    },
    initItemFromPartialItem(partialItem) {
      this.item = {
        description: partialItem.description || '',
        exchange_type: partialItem.exchange_type,
        datetime: formatDateToInputDateTime(partialItem.datetime),
        action_id: partialItem.action_id,
        author_idx: partialItem.authors.map((aut) => aut.author_id),
        logbook_entry_id: partialItem.logbook_entry_id,
        id: partialItem.id,
      };
    },
    generateMessagePath(key) {
      return this.generateMessage(
        `user_exchanges.${this.isEditing ? 'update' : 'create'}.${key}`
      );
    },
    handleValidateClick() {
      this.validate();
    },
  },
};
</script>
